<script>
import App from '@/pages/layouts/app';
import Card from "@/pages/components/card.vue";
import {Link, useForm} from "@inertiajs/vue3";
import Signature from "@/pages/components/form/input-only/signature.vue";
import Modal from "@/pages/components/modal.vue";
import FormElements from "@wielink-application/shared/js/mixins/formElements";


let partTimeout = null;

export default {
  components: {Modal, Signature, App, Card, Link},
  mixins: [FormElements],
  props: ['appointmentId'],
  data() {
    return {
      appointment: {},
      parts: [],
      appointmentLoaded: false,
      form: useForm({
        signature: '',
        parts: {}
      })
    }
  },

  mounted() {
    this.getAppointment();

    this.$watch('appointmentLoaded', async () => {
      await this.getParts();

      this.$watch('form.parts', this.saveParts.bind(this), {deep: true});
    });
  },

  computed: {
    title() {
      if (!this.appointment.customer) {
        return 'Keuring';
      }
      return `Keuring - ${this.appointment.customer.name}`
    },

    saveAvailable() {
      let completed = true;
      for (let key in this.reports) {
        let report = this.reports[key];
        if (!this.isCompleted(report.id, report.product_id)) {
          completed = false;
        }
      }
      return completed;
    },

    reports() {
      if (!this.appointment.products) {
        return [];
      }
      let reports = [];
      for (let productKey in this.appointment.products) {
        let product = this.appointment.products[productKey];

        for (let key in product.product.type.inspection_reports) {
          let inspection_report = product.product.type.inspection_reports[key];

          if (product.product.inspection_type == 'first_inspection' && !inspection_report.first_inspection) {
            continue;
          }

          if (product.product.inspection_type == 're_inspection' && !inspection_report.re_inspection) {
            continue;
          }

          reports.push({
            'id': inspection_report.id,
            'name': inspection_report.name,
            'inspection_type': product.product.inspection_type,
            'product_id': product.product.id,
            'customer_product_id': product.id,
            'serial_number': product.product.serial_number,
          });
        }
      }

      return reports;
    }
  },

  methods: {
    getAppointment() {
      $.ajax({
        url: route('api.v1.appointments.show', {appointment: this.appointmentId}),
        success: (response) => {
          this.appointment = response.data;
          this.appointmentLoaded = true;
        }
      })
    },

    getParts() {
      return new Promise(async (resolve) => {
        await $.ajax({
          url: route('api.v1.parts.index', {length: 999}),
          success: (response) => {
            this.parts = response.data;
          }
        });

        for (let key in this.parts) {
          let part = this.parts[key];
          let lineItem = this.appointment.parts.filter((item) => item.part_id == part.id)[0] ?? null;

          if (!lineItem) {
            this.form.parts[part.id] = 0;
            continue;
          }

          this.form.parts[part.id] = lineItem.amount;
        }

        resolve(true);
      });
    },

    saveParts() {
      partTimeout ? clearTimeout(partTimeout) : null;
      partTimeout = setTimeout(() => {
        $.ajax({
          url: route('api.v1.inspection_reports.save-parts', {appointment: this.appointmentId}),
          method: 'post',
          contentType: 'application/json',
          processData: false,
          data: JSON.stringify({
            '_method': 'PUT',
            'parts': this.form.data().parts
          }),
        });
      }, 200);
    },

    getResponse(inspectionReportId, productId) {
      return this.appointment.inspection_report_responses.filter(response => response.inspection_report_id == inspectionReportId && response.product_id == productId)[0] ?? null;
    },

    isCompleted(inspectionReportId, productId) {
      let response = this.getResponse(inspectionReportId, productId);
      return !(!response || response.is_concept == 1);
    },

    async openModal() {
      let result = await Helpers.Alert.confirm('Is er een nieuw zegel met datum aangebracht?');
      if(!result) {
        return;
      }
      this.$refs.signatureModal.open();
    },

    async submit() {
      let confirmed = await Helpers.Alert.confirm('Let op, als je de keuring afrond kun je geen wijzigingen meer toepassen. Ook zal er een factuur in king worden gegenereerd. Weet je zeker dat je keuring wilt afronden?');
      if (!confirmed) {
        return;
      }

      let data = this.form.data();

      $.ajax({
        url: route('api.v1.inspection_reports.update', {appointment: this.appointmentId}),
        method: 'post',
        contentType: 'application/json',
        processData: false,
        data: JSON.stringify({
          _method: 'PUT',
          ...data
        }),
        success: (response) => {
          this.$refs.signatureModal.close();
          window.location.href = route('dashboard', {success: 'De keuring is succesvol afgerond.'});
        },
        error: (response) => {
          Helpers.Form.handleErrors(this.form, response);
        }
      })
    },

    async startInspection() {
      let confirmed = await Helpers.Alert.confirm('Weet je het zeker?');

      if (!confirmed) {
        return;
      }

      $.ajax({
        url: route('api.v1.appointments.start-inspection', {'appointment': this.appointment.id}),
        method: 'POST',
        success: (response) => {
          this.appointment.inspection_start = response.data.inspection_start;
        }
      })
    }
  }
}
</script>

<template>
  <App :title="title">
    <template #topButtons>
      <button class="btn btn-primary" @click.prevent="startInspection" :class="{'disabled' : !appointment || appointment.inspection_start}">Start keuring</button>
    </template>

    <div class="row mb-4">
      <div class="col-xl-9">
        <div v-if="appointmentLoaded && !appointment.inspection_start" class="alert alert-warning">
          Voordat je kunt beginnen moet je eerst de keuring starten.
        </div>

        <Card class="mb-4">
          <h2 class="card-title">Producten</h2>

          <table class="table table-striped table-sm m-0" v-if="appointment">
            <thead>
              <tr>
                <th class="d-none d-lg-table-cell">Product</th>
                <th>Formulier</th>
                <th class="d-none d-lg-table-cell">Status</th>
                <th style="width: 1px;"></th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="report in reports" :key="`${report.product_id}-${report.id}`" style="vertical-align: middle">
                <td class="d-none d-lg-table-cell">{{ report.serial_number }}</td>
                <td>
                  {{ report.name }}
                  <span class="d-block d-lg-none">
                    <strong>Product:</strong>
                    {{ report.serial_number }} <br>
                    <strong>status:</strong>
                    {{ isCompleted(report.id, report.product_id) ? 'afgerond' : 'open' }}
                  </span>
                </td>
                <td class="d-none d-lg-table-cell">{{ isCompleted(report.id, report.product_id) ? 'afgerond' : 'open' }}</td>
                <td style="width: 1px;">
                  <div class="d-flex gap-2">
                    <Link v-if="!appointment.finished" :href="route('inspection_reports.form', {appointment: appointment.id, customerProduct: report.customer_product_id, inspectionReport: report.id})" class="btn btn-primary" :class="{'disabled': !appointment.inspection_start}">Formulier</Link>

                    <a v-if="isCompleted(report.id, report.product_id)" class="btn btn-secondary py-0" :href="route('inspection_report_responses.pdf', {response: getResponse(report.id, report.product_id).id})" target="_blank">
                      <i class="bi bi-filetype-pdf m-0" style="font-size: 1.4em"></i>
                    </a>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </Card>

        <Card>
          <h2 class="card-title">Onderdelen</h2>

          <template v-for="(part, index) in parts" :key="part.id">
            <Input
                :groupClasses="parts.length - 1 > index ? '' : 'mb-0'"
                :form="form"
                v-model="form.parts[part.id]"
                :name="'part_' + part.id"
                type="number"
                :label="`${part.name} - &euro; ${part.price} p/s`"
                :readonly="appointment.finished"
            />
          </template>
        </Card>
      </div>
    </div>

    <Modal ref="signatureModal" id="signature-modal" title="Handtekening">
      <form id="appointment_form" @submit.prevent="submit">
        <div class="mb-4">
          <Signature
              :form="form"
              v-model="form.signature"
              name="signature"
          />
        </div>
      </form>

      <template #footer>
        <button @click.prevent="$refs.signatureModal.close()" class="btn btn-secondary">Annuleren</button>
        <button form="appointment_form" class="btn btn-primary">Keuring afronden</button>
      </template>
    </Modal>

    <template v-if="appointmentLoaded && !appointment.finished" #bottomButtons>
      <div class="d-flex flex-column-reverse flex-xxl-row align-items-xxl-center column-gap-3 row-gap-1">
        <span>
          <button class="btn btn-primary" :class="{'disabled': !saveAvailable}" @click.prevent="openModal">Keuring afronden</button>
        </span>
        <span v-if="!saveAvailable">
          <i>U kunt het keuringsrapport nog niet afronden omdat nog niet alle formulieren zijn afgerond.</i>
        </span>
      </div>
    </template>
  </App>
</template>

